import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, type NotificationPreferences } from '../api/notifications';

const KEYS = {
  list:        (params: object) => ['notifications', params] as const,
  count:       ()               => ['notifications', 'count'] as const,
  preferences: ()               => ['notifications', 'preferences'] as const,
};

export function useNotifications(params: { unread_only?: 0 | 1; per_page?: number } = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn:  () => notificationsApi.list(params),
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey:        KEYS.count(),
    queryFn:         notificationsApi.count,
    refetchInterval: 60_000, // poll every minute
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: KEYS.preferences(),
    queryFn:  notificationsApi.getPreferences,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      // The Dashboard's own summary payload includes its own unread_notifications
      // snapshot, fetched independently from the bell — without this it goes
      // stale until the Dashboard's own 60s poll catches up.
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<NotificationPreferences>) => notificationsApi.updatePreferences(data),
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: KEYS.preferences() });
    },
  });
}
